<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { useNavigate } from 'svelte-navigator';
  import { _ } from 'svelte-i18n';

  import { AppState, SetIconsBackground } from '../etc/stores';
  import { getParamsVisualTest, setWidgetConfiguration } from '../etc/utils';
  import flags from '../etc/flagKeys';

  import { Logger } from '../services/logger';
  import { Container } from '../services/container';
  import { Backend } from '../services/backend';
  import Analytics from '../services/analytics';
  import { Auth } from '../services/auth';
  import { FreeCoinsCampaign } from '../services/free-coins-campaign';

  import Page from '../components/misc/Page.svelte';
  import Card from '../components/misc/Card.svelte';
  import Header from '../components/misc/Header.svelte';
  import Button from '../components/form/Button.svelte';
  import Text from '../components/misc/Text.svelte';
  import TransactionsIcon from '../components/icons/TransactionsIcon.svelte';
  import GiftIcon from '../components/icons/Gift.svelte';
  import ChevronRight from '../components/icons/ChevronRight.svelte';
  import Label from '../components/typography/Label.svelte';
  import SocialRewardsIcon from '../components/icons/SocialRewardsIcon.svelte';

  import { mockCoupons } from '../mocks/mockVisualTest';
  import FooterWidget from '../components/misc/FooterWidget.svelte';
  import { replaceVariables } from '../i18n';
  import PayWithCoinsIcon from '../components/icons/PayWithCoinsIcon.svelte';
  import PaymentCodeIcon from '../components/icons/PaymentCodeIcon.svelte';
  import { SessionManager } from '../services/session-manager';
  import sessionKeys from '../etc/sessionKeys';
  import { Checkout } from '../services/checkout';
  import { fade } from 'svelte/transition';
  import { Customer } from '../services/customer';

  const navigate = useNavigate();
  const auth = Container.get(Auth);
  const backend = Container.get(Backend);
  const logger = Container.get(Logger).context('Dashboard.svelte') as Logger;
  const freeCoinsCampaign = Container.get(
    FreeCoinsCampaign
  ) as FreeCoinsCampaign;
  const session = Container.get(SessionManager) as SessionManager;
  const checkout = Container.get(Checkout) as Checkout;
  const customer = Container.get(Customer);

  let paymentCodes = $AppState.coupons || [];
  let startNavOutAnimation = false;

  let timeNavAnimation = 600;
  let timeoutAnimation;

  $: hasNavigateBackPage = $AppState.website.navigationBackPage;
  $: hasFreeCoinsCampaignOrCoupon =
    !!$AppState.freeCoinsCampaign.id || !!$AppState.freeCoinsCampaign.coupon;
  $: hasReferralProgram = !!$AppState.referralProgram.id;
  $: hasSocialReward = $AppState.socialReward.length > 0;
  $: hasCoupons = paymentCodes.length > 0;
  $: NoWalletAmount =
    $AppState.website.method === 'signup' &&
    $AppState.customer.wallet.pendingCoins === '0.00' &&
    $AppState.customer.wallet.coins === '0.00' &&
    !hasFreeCoinsCampaignOrCoupon &&
    !hasReferralProgram &&
    !hasSocialReward &&
    !hasCoupons;

  $: showExplanation = NoWalletAmount && $AppState.business.loyaltyEnabled;

  let nav: {
    text: string;
    subtext?: string;
    link: string;
    icon: any;
    count?: number;
    eventName: string;
    notificationBullet?: boolean;
    themed?: boolean;
    notificationBulletColor?: string;
  }[];

  const t = (text: string, values?: object) => {
    return replaceVariables($_(`dashboard.${text}`), {
      BUSINESS_NAME: $AppState.business.name,
      ...values
    });
  };

  let hasFreeCoinsCampaign = false;
  let hasNewTransaction = false;

  $: {
    let countPaymentCodeAvailable = paymentCodes.filter(
      c => c.status === 'active'
    ).length;

    hasNewTransaction =
      (session.get(sessionKeys.CHECKOUT_WITHOUT_TRANSACTION) &&
        !session.get(sessionKeys.CHECKOUT_WITHOUT_TRANSACTION)
          .showedNotification) ||
      $AppState.website.showNotificationTransaction;

    nav = [
      {
        text: t('pay_with_coins'),
        icon: PayWithCoinsIcon,
        link: '/convert-coins',
        eventName: 'click-convert-coins'
      },
      {
        text: t('payment_codes'),
        subtext: hasFreeCoinsCampaign ? t('payment_code_free') : '',
        icon: PaymentCodeIcon,
        link: '/payment-codes',
        count: countPaymentCodeAvailable,
        eventName: 'click-my-coupons',
        notificationBullet: hasFreeCoinsCampaign
      },
      {
        text: t('transactions'),
        subtext: hasNewTransaction ? t('new-notification', { QTD: 1 }) : '',
        notificationBullet: hasNewTransaction,
        notificationBulletColor: '#FFAE0F',
        icon: TransactionsIcon,
        link: '/transactions',
        eventName: 'click-transactions'
      }
    ];

    if (
      hasSocialReward &&
      $AppState.featureFlags.includes(flags.SOCIAL_REWARDS)
    ) {
      nav.push({
        text: t('social'),
        icon: SocialRewardsIcon,
        link: '/social-rewards',
        eventName: 'click-social-rewards'
      });
    }

    if (
      $AppState.referralProgram.isActive &&
      $AppState.featureFlags.includes(flags.BUSINESS_REFERRAL_WIDGET)
    ) {
      nav.push({
        text: t('referral'),
        icon: GiftIcon,
        link: '/referral-program',
        eventName: 'click-referral-program',
        themed: true
      });
    }
  }

  const continueBrowsing = () => {
    AppState.update(state => ({
      ...state,
      website: {
        ...state.website,
        popoverOpen: false
      }
    }));
  };

  const onGAEventClick = eventName => {
    if (eventName) Analytics.logEvent(eventName, 'logged-in-state');
  };

  const updateFreeCoinsCampaign = async () => {
    await freeCoinsCampaign.loadState();
    hasFreeCoinsCampaign = !!$AppState.freeCoinsCampaign.id;
  };

  const onNavigationAnimated = (link: string) => {
    clearTimeout(timeoutAnimation);
    startNavOutAnimation = true;
    timeoutAnimation = setTimeout(() => {
      navigate(link, {
        state: {
          navAnimation: true
        }
      });
    }, timeNavAnimation);
  };

  const onClickCard = (item: any) => {
    onGAEventClick(item.eventName);
    onNavigationAnimated(item.link);
  };

  const onClickHowItWorks = () => {
    onNavigationAnimated('/how-it-works');
  };

  const clearNavigationBack = () => {
    AppState.update(state => ({
      ...state,
      website: {
        ...state.website,
        navigationBackPage: false
      }
    }));
  };

  onDestroy(() => {
    setWidgetConfiguration(true, false);
    clearNavigationBack();
    clearTimeout(timeoutAnimation);
  });

  onMount(async () => {
    SetIconsBackground('outline');
    setWidgetConfiguration(false);
    updateFreeCoinsCampaign();
    Analytics.logPageView('Dashboard', '/dashboard');

    try {
      await checkout.verifyProvisionalCheckoutWix();

      if (getParamsVisualTest().enabled) {
        paymentCodes = mockCoupons;
      } else if (!(await auth.isSoftLogin())) {
        customer.loadWallet();
        const response = await backend.getCoupons($AppState.business.id);

        paymentCodes = response.coupons;

        AppState.update(state => ({
          ...state,
          coupons: paymentCodes
        }));
      }
    } catch (e) {
      logger.error(e);
      paymentCodes = [];
    }
  });
</script>

<div class="twism__dashboard__container">
  <div class="twism__dashboard__container__top">
    <Header
      {NoWalletAmount}
      {startNavOutAnimation}
      startNavInAnimation={hasNavigateBackPage}
      {timeNavAnimation}
      on:coin-status-clicked={() => onNavigationAnimated('/transactions')}
    />
    <div
      class="twism__dashboard__container__bottom {!hasReferralProgram
        ? 'twism-expanded'
        : ''}"
      in:fade={{
        duration: hasNavigateBackPage ? 200 : 0,
        delay: hasNavigateBackPage ? 100 : 0
      }}
      class:--animation={startNavOutAnimation}
      style={`--animation-time: ${timeNavAnimation / 2}ms`}
    >
      {#if showExplanation}
        <Page
          hideCloseButton
          padding="8px 24px"
          closeStyle="top: 29px;"
          className="dashboard__container"
        >
          <div class="dashboard__description">
            <Text
              fontSize="20px"
              lineHeight="28px"
              fontWeight="600"
              color="#012A5A"
              textAlign="center"
            >
              {t('coin_for_every')}
            </Text>
          </div>
          <div class="dashboard__explanation">
            <Text fontSize="15px" lineHeight="22.5px" color="#7A89B0">
              {t('automatically_rewarded')}
            </Text>
          </div>
          <div class="dashboard__button">
            <Button
              label={t('continue_browsing')}
              on:click={continueBrowsing}
            />
          </div>
        </Page>
      {:else}
        <Page
          hideCloseButton
          padding="0 24px 4px"
          closeStyle="top: 29px;"
          className="dashboard__container"
        >
          {#each nav as item (item.link)}
            <div
              class="twism-row twism-wide {item.themed
                ? 'twism-main'
                : ''} {!hasReferralProgram ? 'twism-expanded' : ''}"
            >
              <Card
                style="z-index: -1; {!hasReferralProgram
                  ? 'height: 68px;'
                  : ''}"
                variant={item.themed ? 'themed' : ''}
                on:click={() => onClickCard(item)}
              >
                <div class="twism-row">
                  <div class="twism-row-icon">
                    <svelte:component
                      this={item.icon}
                      color={$AppState.theme.colors.textAboveBackground}
                    />
                  </div>
                  {#if item.notificationBullet}
                    <div
                      class="twism-row-notification-bullet"
                      style="background-color: {item.notificationBulletColor ||
                        $AppState.theme.colors.secondary};"
                    />
                  {/if}
                  <div class="twism-text-wrapper">
                    <Label
                      type="medium-regular-semibold"
                      class={item.themed && 'twism-themed-text'}
                      color={item.themed && '#FFF'}
                    >
                      {@html item.text}
                    </Label>
                    {#if item.subtext}
                      <p class="twism-subtext">{item.subtext}</p>
                    {/if}
                  </div>
                </div>
                {#if item.count}
                  <div class="dashboard__coupons">
                    <span>{item.count}</span>
                  </div>
                {:else}
                  <ChevronRight
                    color={item.themed
                      ? '#fff'
                      : $AppState.theme.colors.primary500}
                  />
                {/if}
              </Card>
            </div>
          {/each}
        </Page>
      {/if}
    </div>
  </div>
  <div
    class="twism__dashboard__container--footer"
    class:--animation={startNavOutAnimation}
    style={`--animation-time: ${timeNavAnimation / 2}ms`}
    in:fade={{
      duration: hasNavigateBackPage ? 200 : 0,
      delay: hasNavigateBackPage ? 100 : 0
    }}
  >
    <FooterWidget {onClickHowItWorks} />
  </div>
</div>

<style lang="scss">
  :global(.twism-page.dashboard__container) {
    height: unset !important;
  }

  :global(.twism-themed-text) {
    font-weight: 400 !important;
  }

  .twism__dashboard__container {
    display: flex;
    flex-flow: column nowrap;
    justify-content: space-between;
    overflow-y: auto;
    overflow-x: hidden;
    height: 100%;
    overscroll-behavior: contain;

    &::-webkit-scrollbar {
      display: none;
    }

    &--footer {
      transition: opacity 0.3s ease-in-out;

      &.--animation {
        opacity: 0;
      }
    }
    &__bottom {
      padding-top: 16px;

      transition: opacity 0.3s ease-in-out;

      &.--animation {
        opacity: 0;
      }

      &.twism-expanded {
        padding-top: 24px;
      }
    }

    &__top {
      padding-bottom: 70px;
    }
  }

  .dashboard {
    &__title {
      align-self: flex-start;
      margin-bottom: 8px;
      h4 {
        font-size: 20px;
        line-height: 24px;
        color: $color-firefly;
        font-weight: bold;
        margin-bottom: 4px;
        margin-top: 0;
      }
      p {
        font-size: 14px;
        line-height: 22px;
        color: $color-blue-bayoux;
        margin-bottom: 0;
      }
    }
    &__coupons {
      flex: 1;
      padding: 4px 0;
      max-height: 24px;
      height: 24px;
      box-sizing: border-box;
      border: 1px solid var(--twism-theme-slightBorder);
      border-radius: 7px;
      text-align: center;
      span {
        min-width: 22px;
        display: block;
        font-size: 14px;
        line-height: 14px;
        font-weight: 600;
        color: var(--twism-theme-secondary);
      }
    }
    &__image {
      margin-top: 5px;
    }
    &__description {
      width: 260px;
      margin-top: 48px;
    }
    &__explanation {
      width: 250px;
      margin-top: 8px;
    }
    &__button {
      width: 100%;
      margin-top: 24px;
    }
  }

  .twism-subtext {
    margin-top: 4px;
    font-size: 12px;
    line-height: 14px;
    font-weight: 400;
    color: #7a89b0;
  }

  .twism-row {
    display: flex;
    align-items: center;
    position: relative;
    width: 100%;

    &-icon {
      min-width: 32px;
    }

    &-notification-bullet {
      width: 8px;
      height: 8px;
      border: 2px solid white;
      box-sizing: content-box;
      border-radius: 50%;
      position: absolute;
      left: 24px;
      top: -3px;
    }

    p {
      padding: 0;
      @media screen and (max-width: 468px) {
        font-size: 13px;
      }
    }

    &.twism-wide {
      z-index: 1;
      justify-content: space-between;
      margin-bottom: 8px;

      @media screen and (max-width: 468px) and (min-height: 600px) {
        margin-bottom: 24px;
      }

      .twism-text-wrapper {
        margin-left: 14px;
      }
    }

    &.twism-main {
      z-index: 5;

      p {
        font-weight: 400;
        font-size: 14px;
        line-height: 19px;
      }
    }

    &.twism-expanded {
      margin-bottom: 16px;
    }
  }
</style>
